import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Grid,
  FormControlLabel,
  Checkbox,
  Typography,
  Alert,
} from '@mui/material';
import { SignatureEditor } from './SignatureEditor';
import { SignaturePreview } from './SignaturePreview';
import type {
  UserSignature,
  SystemSignature,
} from '../../../../../store/reducers/signatures.store';

interface CreateSignatureModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (signature: {
    name: string;
    content: string;
    isDefault: boolean;
    email?: string;
    firstName?: string;
    lastName?: string;
  }) => Promise<void>;
  editingSignature?: UserSignature | SystemSignature | null;
  loading?: boolean;
  isSystemSignature?: boolean;
  defaultValues?: {
    email?: string;
    firstName?: string;
    lastName?: string;
  };
}

export const CreateSignatureModal: React.FC<CreateSignatureModalProps> = ({
  open,
  onClose,
  onSave,
  editingSignature,
  loading = false,
  isSystemSignature = false,
  defaultValues,
}) => {
  const [name, setName] = useState('');
  const [content, setContent] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [contentError, setContentError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [firstNameError, setFirstNameError] = useState<string | null>(null);
  const [lastNameError, setLastNameError] = useState<string | null>(null);
  const [previewMode, setPreviewMode] = useState(false);
  const initializedRef = useRef(false);

  const isEditing = !!editingSignature;

  useEffect(() => {
    if (open && !initializedRef.current) {
      console.log('🔍 CreateSignatureModal Debug:', {
        editingSignature,
        isEditing,
        defaultValues,
        isSystemSignature,
      });

      if (editingSignature) {
        setName(editingSignature.name);
        setContent(editingSignature.content);
        setIsDefault(editingSignature.isDefault);
        if (isSystemSignature) {
          // Type assertion to access SystemSignature properties
          const systemSignature = editingSignature as SystemSignature;
          setEmail(systemSignature.email || '');
          setFirstName(systemSignature.firstName || '');
          setLastName(systemSignature.lastName || '');
        }
      } else {
        setName('');
        setContent('');
        setIsDefault(false);
        // Use default values if provided, otherwise clear fields
        setEmail(defaultValues?.email || '');
        setFirstName(defaultValues?.firstName || '');
        setLastName(defaultValues?.lastName || '');
      }
      // Clear all error states
      setNameError(null);
      setContentError(null);
      setEmailError(null);
      setFirstNameError(null);
      setLastNameError(null);
      setPreviewMode(false);

      console.log('🔍 Initial state after clearing errors:', {
        nameError: null,
        contentError: null,
        emailError: null,
        firstNameError: null,
        lastNameError: null,
        email: defaultValues?.email || '',
        firstName: defaultValues?.firstName || '',
        lastName: defaultValues?.lastName || '',
      });

      initializedRef.current = true;
    }

    if (!open) {
      initializedRef.current = false;
    }
  }, [open, editingSignature, isSystemSignature, defaultValues]);

  // Additional effect to clear errors when default values are set
  useEffect(() => {
    if (open && !editingSignature && defaultValues) {
      // Clear errors after default values are applied
      setTimeout(() => {
        setNameError(null);
        setContentError(null);
        setEmailError(null);
        setFirstNameError(null);
        setLastNameError(null);
        console.log('🔍 Errors cleared:', {
          nameError: null,
          contentError: null,
          emailError: null,
          firstNameError: null,
          lastNameError: null,
        });
      }, 0);
    }
  }, [open, editingSignature, defaultValues]);

  // Debug effect to log error states
  useEffect(() => {
    if (open) {
      console.log('🔍 Error states:', {
        nameError,
        contentError,
        emailError,
        firstNameError,
        lastNameError,
        email,
        firstName,
        lastName,
        emailErrorProp: emailError !== null,
        firstNameErrorProp: firstNameError !== null,
        lastNameErrorProp: lastNameError !== null,
      });
    }
  }, [
    open,
    nameError,
    contentError,
    emailError,
    firstNameError,
    lastNameError,
    email,
    firstName,
    lastName,
  ]);

  const validateForm = () => {
    let isValid = true;

    if (!name.trim()) {
      setNameError('Signature name is required');
      isValid = false;
    } else if (name.length > 100) {
      setNameError('Signature name must be less than 100 characters');
      isValid = false;
    } else {
      setNameError(null);
    }

    if (!content.trim()) {
      setContentError('Signature content is required');
      isValid = false;
    } else if (content.length > 10000) {
      setContentError('Signature content must be less than 10KB');
      isValid = false;
    } else {
      setContentError(null);
    }

    // Validate Team Signature specific fields
    if (isSystemSignature) {
      if (!email.trim()) {
        setEmailError('Email is required');
        isValid = false;
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
        setEmailError('Please enter a valid email address');
        isValid = false;
      } else {
        setEmailError(null);
      }

      if (!firstName.trim()) {
        setFirstNameError('First name is required');
        isValid = false;
      } else if (firstName.length > 50) {
        setFirstNameError('First name must be less than 50 characters');
        isValid = false;
      } else {
        setFirstNameError(null);
      }

      if (!lastName.trim()) {
        setLastNameError('Last name is required');
        isValid = false;
      } else if (lastName.length > 50) {
        setLastNameError('Last name must be less than 50 characters');
        isValid = false;
      } else {
        setLastNameError(null);
      }
    }

    return isValid;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      const signatureData: any = {
        name: name.trim(),
        content: content.trim(),
        isDefault,
      };

      // Add Team Signature specific fields
      if (isSystemSignature) {
        signatureData.email = email.trim();
        signatureData.firstName = firstName.trim();
        signatureData.lastName = lastName.trim();
      }

      await onSave(signatureData);
      onClose();
    } catch (error) {
      // Error handling is done in the parent component
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '600px',
        },
      }}
    >
      <DialogTitle sx={{ pl: 3 }}>
        {isEditing
          ? `Edit ${isSystemSignature ? 'Team' : ''} Signature`
          : `Create New ${isSystemSignature ? 'Team' : ''} Signature`}
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                label="Signature Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                error={nameError !== null}
                helperText={
                  nameError || 'Give your signature a descriptive name'
                }
                FormHelperTextProps={{
                  sx: {
                    color: nameError ? 'error.main' : 'text.secondary',
                  },
                }}
                fullWidth
                disabled={loading}
                placeholder="e.g., Professional, Personal, Marketing"
              />
            </Grid>

            {/* Team Signature specific fields */}
            {isSystemSignature && (
              <>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="From Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    error={emailError !== null}
                    helperText={emailError || 'Email address for the signature'}
                    FormHelperTextProps={{
                      sx: {
                        color: emailError ? 'error.main' : 'text.secondary',
                      },
                    }}
                    fullWidth
                    disabled={loading}
                    placeholder="user@example.com"
                    type="email"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="From First Name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    error={firstNameError !== null}
                    helperText={
                      firstNameError || 'First name for the signature'
                    }
                    FormHelperTextProps={{
                      sx: {
                        color: firstNameError ? 'error.main' : 'text.secondary',
                      },
                    }}
                    fullWidth
                    disabled={loading}
                    placeholder="John"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="From Last Name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    error={lastNameError !== null}
                    helperText={lastNameError || 'Last name for the signature'}
                    FormHelperTextProps={{
                      sx: {
                        color: lastNameError ? 'error.main' : 'text.secondary',
                      },
                    }}
                    fullWidth
                    disabled={loading}
                    placeholder="Doe"
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Box display="flex" gap={2} mb={2}>
                <Button
                  variant={!previewMode ? 'contained' : 'outlined'}
                  onClick={() => setPreviewMode(false)}
                  size="small"
                >
                  Edit
                </Button>
                <Button
                  variant={previewMode ? 'contained' : 'outlined'}
                  onClick={() => setPreviewMode(true)}
                  size="small"
                >
                  Preview
                </Button>
              </Box>

              {previewMode ? (
                <SignaturePreview content={content} showTitle={false} />
              ) : (
                <SignatureEditor
                  value={content}
                  onChange={setContent}
                  disabled={loading}
                  placeholder="Enter your signature content..."
                />
              )}

              {contentError && (
                <Typography
                  color="error"
                  variant="caption"
                  display="block"
                  sx={{ mt: 1 }}
                >
                  {contentError}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isDefault}
                    onChange={(e) => setIsDefault(e.target.checked)}
                    disabled={loading}
                  />
                }
                label="Set as default signature"
              />
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Default signature will be automatically inserted when composing
                new emails
              </Typography>
            </Grid>

            {isDefault && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mt: 1 }}>
                  Setting this as default will remove the default status from
                  any other signature.
                </Alert>
              </Grid>
            )}
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={
            loading ||
            !name.trim() ||
            !content.trim() ||
            (isSystemSignature &&
              (!email.trim() || !firstName.trim() || !lastName.trim()))
          }
        >
          {loading
            ? 'Saving...'
            : isEditing
            ? `Update ${isSystemSignature ? 'Team' : ''} Signature`
            : `Create ${isSystemSignature ? 'Team' : ''} Signature`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
